import { useRef } from "react";
import { ImageIcon, Loader2, LayoutTemplate, Type, Upload, X } from "lucide-react";
import { cn } from "@/lib/utils";
import type { ProposalDocumentLayoutPage } from "../../types";
import { useBuilderImageUpload } from "../../hooks/useBuilderImageUpload";

interface BuilderCoverPageSettingsProps {
  page: ProposalDocumentLayoutPage;
  readOnly?: boolean;
  onChange: (updates: Partial<ProposalDocumentLayoutPage>) => void;
  className?: string;
}

type CoverPageSettings = Record<string, string | boolean | number | null>;

function getSettings(page: ProposalDocumentLayoutPage): CoverPageSettings {
  return page.settings && typeof page.settings === "object"
    ? (page.settings as CoverPageSettings)
    : {};
}

function getStringSetting(
  settings: CoverPageSettings,
  key: string,
  fallback = ""
) {
  const value = settings[key];
  return typeof value === "string" ? value : fallback;
}

function getBooleanSetting(
  settings: CoverPageSettings,
  key: string,
  fallback: boolean
) {
  const value = settings[key];
  return typeof value === "boolean" ? value : fallback;
}

function updateSetting(
  settings: CoverPageSettings,
  key: string,
  value: string | boolean | number | null
) {
  return {
    settings: {
      ...settings,
      [key]: value,
    },
  };
}

function SectionCard({
  title,
  description,
  children,
}: {
  title: string;
  description?: string;
  children: React.ReactNode;
}) {
  return (
    <section className="overflow-hidden rounded-2xl border border-white/10 bg-white/[0.03]">
      <div className="border-b border-white/10 px-4 py-3">
        <p className="text-sm font-medium text-white/88">{title}</p>
        {description ? (
          <p className="mt-1 text-xs leading-5 text-white/40">{description}</p>
        ) : null}
      </div>

      <div className="space-y-4 p-4">{children}</div>
    </section>
  );
}

function FieldLabel({
  label,
  icon,
  hint,
}: {
  label: string;
  icon?: React.ReactNode;
  hint?: string;
}) {
  return (
    <div className="mb-2">
      <label className="flex items-center gap-2 text-xs font-medium uppercase tracking-[0.14em] text-white/35">
        {icon}
        {label}
      </label>
      {hint ? <p className="mt-1 text-xs text-white/35">{hint}</p> : null}
    </div>
  );
}

function ToggleField({
  label,
  description,
  checked,
  disabled,
  onChange,
}: {
  label: string;
  description: string;
  checked: boolean;
  disabled: boolean;
  onChange: (checked: boolean) => void;
}) {
  return (
    <label className="flex items-start gap-3 rounded-xl border border-white/10 bg-black/20 p-4">
      <input
        type="checkbox"
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="mt-0.5 h-4 w-4 rounded border-white/20 bg-black/20 text-white"
      />

      <div>
        <p className="text-sm font-medium text-white/86">{label}</p>
        <p className="mt-1 text-xs leading-5 text-white/40">{description}</p>
      </div>
    </label>
  );
}

export function BuilderCoverPageSettings({
  page,
  readOnly = false,
  onChange,
  className,
}: BuilderCoverPageSettingsProps) {
  const settings = getSettings(page);
  const uploadImage = useBuilderImageUpload();
  const fileInputRef = useRef<HTMLInputElement>(null);

  const headline = getStringSetting(settings, "headline");
  const subheadline = getStringSetting(settings, "subheadline");
  const backgroundImageUrl = getStringSetting(settings, "backgroundImageUrl");
  const showLogo = getBooleanSetting(settings, "showLogo", true);
  const showProspectDetails = getBooleanSetting(
    settings,
    "showProspectDetails",
    true
  );

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    uploadImage.mutate(file, {
      onSuccess: ({ publicUrl }) => {
        onChange(updateSetting(settings, "backgroundImageUrl", publicUrl));
      },
    });
    // Reset so the same file can be re-picked if cleared
    e.target.value = "";
  }

  return (
    <div
      className={cn(
        "overflow-hidden rounded-2xl border border-white/10 bg-black/20",
        className
      )}
    >
      <div className="border-b border-white/10 bg-gradient-to-r from-sky-500/10 via-indigo-500/6 to-transparent px-4 py-3">
        <div className="flex items-start gap-3">
          <div className="rounded-lg border border-white/10 bg-white/[0.06] p-2 text-white/65">
            <LayoutTemplate className="h-4 w-4" />
          </div>

          <div className="min-w-0">
            <p className="text-sm font-medium text-white/88">
              Cover Page Settings
            </p>
            <p className="mt-1 text-xs leading-5 text-white/40">
              Adjust the opening message, visual background, and visibility rules
              that shape the first impression on the stage.
            </p>
          </div>
        </div>
      </div>

      <div className="space-y-4 p-4">
        <SectionCard
          title="Opening message"
          description="Set the primary message the client sees first on the cover page."
        >
          <div>
            <FieldLabel
              label="Headline"
              icon={<Type className="h-4 w-4" />}
              hint="Main statement or promise for the proposal cover."
            />
            <input
              value={headline}
              onChange={(e) =>
                onChange(updateSetting(settings, "headline", e.target.value))
              }
              disabled={readOnly}
              placeholder="Your proposal headline"
              className="w-full rounded-xl border border-white/10 bg-black/20 px-4 py-2.5 text-sm text-white placeholder:text-white/25 focus:border-white/25 focus:outline-none focus:ring-1 focus:ring-white/20 disabled:opacity-40"
            />
          </div>

          <div>
            <FieldLabel
              label="Subheadline"
              hint="Optional supporting line under the main cover message."
            />
            <textarea
              rows={4}
              value={subheadline}
              onChange={(e) =>
                onChange(updateSetting(settings, "subheadline", e.target.value))
              }
              disabled={readOnly}
              placeholder="Optional supporting message for the cover page"
              className="w-full rounded-xl border border-white/10 bg-black/20 px-4 py-3 text-sm text-white placeholder:text-white/25 focus:border-white/25 focus:outline-none focus:ring-1 focus:ring-white/20 disabled:opacity-40"
            />
          </div>
        </SectionCard>

        <SectionCard
          title="Background image"
          description="Use an image when the cover should feel more branded or visual."
        >
          {/* Upload from device */}
          <input
            ref={fileInputRef}
            type="file"
            accept="image/*"
            className="hidden"
            onChange={handleFileChange}
            disabled={readOnly || uploadImage.isPending}
          />

          <div>
            <FieldLabel
              label="Background image"
              icon={<ImageIcon className="h-4 w-4" />}
              hint="Upload from your device or paste an image URL."
            />

            {/* Upload button */}
            <button
              type="button"
              onClick={() => fileInputRef.current?.click()}
              disabled={readOnly || uploadImage.isPending}
              className="mb-2 inline-flex w-full items-center justify-center gap-2 rounded-xl border border-white/10 bg-white/[0.05] px-4 py-2.5 text-sm text-white/70 transition hover:bg-white/[0.09] hover:text-white disabled:opacity-40"
            >
              {uploadImage.isPending ? (
                <>
                  <Loader2 className="h-4 w-4 animate-spin" />
                  Uploading…
                </>
              ) : (
                <>
                  <Upload className="h-4 w-4" />
                  Upload image from device
                </>
              )}
            </button>

            {uploadImage.isError && (
              <p className="mb-2 text-xs text-red-400">
                {uploadImage.error?.message ?? "Upload failed — try again."}
              </p>
            )}

            {/* URL input */}
            <div className="relative">
              <input
                value={backgroundImageUrl}
                onChange={(e) =>
                  onChange(
                    updateSetting(settings, "backgroundImageUrl", e.target.value)
                  )
                }
                disabled={readOnly || uploadImage.isPending}
                placeholder="…or paste an image URL"
                className="w-full rounded-xl border border-white/10 bg-black/20 py-2.5 pl-4 pr-9 text-sm text-white placeholder:text-white/25 focus:border-white/25 focus:outline-none focus:ring-1 focus:ring-white/20 disabled:opacity-40"
              />
              {backgroundImageUrl && (
                <button
                  type="button"
                  onClick={() => onChange(updateSetting(settings, "backgroundImageUrl", ""))}
                  disabled={readOnly}
                  className="absolute right-2.5 top-1/2 -translate-y-1/2 rounded p-0.5 text-white/30 hover:text-white/70"
                  aria-label="Clear background image"
                >
                  <X className="h-3.5 w-3.5" />
                </button>
              )}
            </div>
          </div>

          {/* Preview thumbnail */}
          {backgroundImageUrl && (
            <div
              className="h-24 w-full overflow-hidden rounded-xl border border-white/10 bg-cover bg-center"
              style={{ backgroundImage: `url(${backgroundImageUrl})` }}
            />
          )}

          <div className="rounded-xl border border-white/10 bg-black/20 px-4 py-3">
            <p className="text-xs leading-5 text-white/40">
              Leave this blank for a cleaner text-first cover, or add a strong
              branded image for more visual impact.
            </p>
          </div>
        </SectionCard>

        <SectionCard
          title="Visible cover details"
          description="Choose which supporting items appear around the main cover message."
        >
          <ToggleField
            label="Show company logo"
            description="Display the company logo in the cover layout."
            checked={showLogo}
            disabled={readOnly}
            onChange={(checked) =>
              onChange(updateSetting(settings, "showLogo", checked))
            }
          />

          <ToggleField
            label="Show prospect details"
            description="Display customer or prospect details on the cover page."
            checked={showProspectDetails}
            disabled={readOnly}
            onChange={(checked) =>
              onChange(updateSetting(settings, "showProspectDetails", checked))
            }
          />
        </SectionCard>
      </div>
    </div>
  );
}